import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import vacanciesService from '../services/vacancies.service';
import ReferralSubmitModal from '../components/ReferralSubmitModal';
import ApplicationModal from '../components/ApplicationModal';
import GetReferredModal from '../components/GetReferredModal';
import CheckoutModal from '../components/Payment/CheckoutModal';
import ChatPanel from '../components/Chat/ChatPanel';
import CandidateRating from '../components/Profile/CandidateRating';
import CandidateProfileModal from '../components/Profile/CandidateProfileModal';
import { Vacancy } from '../types/VacancyTypes';
import { ReferralSubmission, ReferralSubmitData, ReferralSubmissionStatus } from '../types/ReferralTypes';
import { formatCurrency, getStatusColor } from '../utils/formatters';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

interface VacancyDetailProps {
  darkMode: boolean;
  onLoginClick: () => void;
}

const PIPELINE_STAGES: { key: ReferralSubmissionStatus; label: string }[] = [
  { key: 'submitted', label: 'New' },
  { key: 'reviewing', label: 'Screening' },
  { key: 'interviewing', label: 'Interview' },
  { key: 'hired', label: 'Hired' },
  { key: 'bounty_paid', label: 'Paid' },
];

const VacancyDetail: React.FC<VacancyDetailProps> = ({ darkMode, onLoginClick }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [vacancy, setVacancy] = useState<Vacancy | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showGetReferredModal, setShowGetReferredModal] = useState(false);
  const [referralSuccess, setReferralSuccess] = useState(false);

  // Poster management state
  const [receivedReferrals, setReceivedReferrals] = useState<ReferralSubmission[]>([]);
  const [checkoutReferral, setCheckoutReferral] = useState<ReferralSubmission | null>(null);
  const [chatReferral, setChatReferral] = useState<ReferralSubmission | null>(null);
  const [profileReferral, setProfileReferral] = useState<ReferralSubmission | null>(null);
  const [filterStage, setFilterStage] = useState<ReferralSubmissionStatus | 'all'>('all');

  const isPoster = vacancy && user && vacancy.posted_by === user.id;

  useEffect(() => {
    const fetch = async () => {
      if (!id) return;
      setLoading(true);
      const data = await vacanciesService.getVacancy(id);
      setVacancy(data);
      setLoading(false);
    };
    fetch();
  }, [id]);

  useEffect(() => {
    if (isPoster) {
      const stored: ReferralSubmission[] = JSON.parse(localStorage.getItem('my_referrals') || '[]');
      setReceivedReferrals(stored.filter(r => r.vacancy_id === id));
    }
  }, [isPoster, id]);

  const handleApplyClick = () => {
    if (!isAuthenticated) {
      onLoginClick();
      return;
    }
    setShowApplicationModal(true);
  };

  const handleReferClick = () => {
    if (!isAuthenticated) {
      onLoginClick();
      return;
    }
    setShowReferralModal(true);
  };

  const handleGetReferredClick = () => {
    if (!isAuthenticated) {
      onLoginClick();
      return;
    }
    setShowGetReferredModal(true);
  };

  const handleSubmitReferral = async (data: ReferralSubmitData) => {
    if (!id) return;
    await vacanciesService.submitReferral(id, data);
    setReferralSuccess(true);
    if (vacancy) {
      setVacancy({ ...vacancy, referral_count: vacancy.referral_count + 1 });
    }
  };

  const handleHireSuccess = () => {
    const stored: ReferralSubmission[] = JSON.parse(localStorage.getItem('my_referrals') || '[]');
    setReceivedReferrals(stored.filter(r => r.vacancy_id === id));
    setCheckoutReferral(null);
  };

  const handleUpdateStage = (referralId: string, newStatus: ReferralSubmissionStatus) => {
    // Update in localStorage (mock mode)
    const allReferrals: ReferralSubmission[] = JSON.parse(localStorage.getItem('my_referrals') || '[]');
    const updated = allReferrals.map(r =>
      r.id === referralId ? { ...r, status: newStatus, updated_at: new Date().toISOString() } : r
    );
    localStorage.setItem('my_referrals', JSON.stringify(updated));
    setReceivedReferrals(updated.filter(r => r.vacancy_id === id));
  };

  const pipelineCounts = PIPELINE_STAGES.reduce((acc, stage) => {
    acc[stage.key] = receivedReferrals.filter(r => r.status === stage.key).length;
    return acc;
  }, {} as Record<string, number>);

  const filteredReferrals = filterStage === 'all'
    ? receivedReferrals
    : receivedReferrals.filter(r => r.status === filterStage);

  const rejectedCount = receivedReferrals.filter(r => r.status === 'rejected').length;

  if (loading) {
    return <div className={`page-container ${darkMode ? 'dark' : ''}`}><div className="loading-state">Loading...</div></div>;
  }

  if (!vacancy) {
    return (
      <div className={`page-container ${darkMode ? 'dark' : ''}`}>
        <div className="empty-state">
          <h3>Vacancy not found</h3>
          <button className="btn-primary" onClick={() => navigate('/vacancies')}>Browse Vacancies</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`page-container ${darkMode ? 'dark' : ''}`}>
      <button className="btn-back" onClick={() => navigate('/vacancies')}>
        <ArrowBackIcon sx={{ fontSize: 18 }} /> Back to Vacancies
      </button>

      <div className="vacancy-detail">
        <div className="vacancy-detail-header">
          <div>
            <div className="vacancy-detail-company">
              <span>{vacancy.company_name}</span>
              {vacancy.is_verified_poster && (
                <VerifiedIcon sx={{ fontSize: 18, color: '#1976d2', marginLeft: '6px' }} />
              )}
            </div>
            <h1>{vacancy.title}</h1>
          </div>
          <div className="vacancy-detail-bounty">
            {vacancy.bounty_amount > 0 ? (
              <>
                <span className="bounty-label">Referral Bounty</span>
                <span className="bounty-amount">{formatCurrency(vacancy.bounty_amount, vacancy.bounty_currency)}</span>
              </>
            ) : vacancy.source === 'scraped' ? (
              <span className="bounty-label">Direct Apply</span>
            ) : (
              <>
                <span className="bounty-label">Referral Bounty</span>
                <span className="bounty-amount">{formatCurrency(0, vacancy.bounty_currency)}</span>
              </>
            )}
          </div>
        </div>

        <div className="vacancy-detail-meta">
          <span className="meta-chip">
            <LocationOnOutlinedIcon sx={{ fontSize: 16 }} />
            {vacancy.is_remote ? 'Remote' : vacancy.location || 'On-site'}
          </span>
          <span className="meta-chip">
            <WorkOutlineIcon sx={{ fontSize: 16 }} />
            {vacancy.employment_type}
          </span>
          <span className="meta-chip">
            <PeopleOutlineIcon sx={{ fontSize: 16 }} />
            {vacancy.referral_count} referral{vacancy.referral_count !== 1 ? 's' : ''} submitted
          </span>
          <span className="meta-chip">
            <AttachMoneyIcon sx={{ fontSize: 16 }} />
            {vacancy.bounty_currency}
          </span>
        </div>

        <div className="vacancy-detail-section">
          <h2>Description</h2>
          <p>{vacancy.description}</p>
        </div>

        {vacancy.requirements && (
          <div className="vacancy-detail-section">
            <h2>Requirements</h2>
            <p>{vacancy.requirements}</p>
          </div>
        )}

        <div className="vacancy-detail-section">
          <p className="posted-info">
            Posted by {vacancy.posted_by_name} on {new Date(vacancy.created_at).toLocaleDateString()}
          </p>
          {vacancy.source === 'scraped' && vacancy.source_url && (
            <a
              href={vacancy.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '12px' }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
              View Original Posting
            </a>
          )}
        </div>

        {/* Poster: Candidate Pipeline & Management */}
        {isPoster && receivedReferrals.length > 0 && (
          <div className="vacancy-detail-section manage-referrals-section">
            <h2>Candidate Pipeline</h2>

            {/* Pipeline Visualization */}
            <div className="pipeline-visual">
              {PIPELINE_STAGES.map((stage, idx) => (
                <React.Fragment key={stage.key}>
                  {idx > 0 && <div className="pipeline-arrow">&#8594;</div>}
                  <button
                    className={`pipeline-stage ${filterStage === stage.key ? 'active' : ''}`}
                    onClick={() => setFilterStage(filterStage === stage.key ? 'all' : stage.key)}
                  >
                    <div className="stage-count">{pipelineCounts[stage.key] || 0}</div>
                    <div className="stage-header">{stage.label}</div>
                  </button>
                </React.Fragment>
              ))}
              {rejectedCount > 0 && (
                <>
                  <div className="pipeline-arrow" style={{ opacity: 0.4 }}>|</div>
                  <button
                    className={`pipeline-stage rejected ${filterStage === 'rejected' ? 'active' : ''}`}
                    onClick={() => setFilterStage(filterStage === 'rejected' ? 'all' : 'rejected')}
                  >
                    <div className="stage-count">{rejectedCount}</div>
                    <div className="stage-header">Rejected</div>
                  </button>
                </>
              )}
            </div>

            {filterStage !== 'all' && (
              <div style={{ margin: '12px 0', fontSize: '0.875rem', color: 'var(--text-secondary, #666)' }}>
                Showing: <strong>{filterStage.replace('_', ' ')}</strong>
                {' '}&mdash;{' '}
                <button
                  className="toggle-mode-btn"
                  onClick={() => setFilterStage('all')}
                  style={{ fontSize: '0.875rem' }}
                >
                  Show all
                </button>
              </div>
            )}

            {/* Candidates List */}
            <div className="referrals-list">
              {filteredReferrals.map(r => (
                <div key={r.id} className="referral-item">
                  <div className="referral-item-header">
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flex: 1 }}>
                      <img
                        src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(r.candidate_email)}`}
                        alt={r.candidate_name}
                        style={{ width: 48, height: 48, borderRadius: '50%', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <h3>{r.candidate_name}</h3>
                          <span className={`application-type-chip ${r.type === 'self_apply' ? 'self-apply' : 'referral'}`}>
                            {r.type === 'self_apply' ? 'Applied' : `Referred by ${r.referrer_name}`}
                          </span>
                        </div>
                        <p className="referral-vacancy">{r.candidate_email}</p>
                        <p className="referral-vacancy" style={{ fontSize: '0.8rem', marginTop: '2px' }}>
                          {new Date(r.created_at).toLocaleDateString()}
                        </p>
                        {r.note && <p className="referral-note">{r.note}</p>}
                      </div>
                    </div>
                    <div className="referral-item-right" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                      <span className="status-badge" style={{ backgroundColor: getStatusColor(r.status) }}>
                        {r.status.replace('_', ' ')}
                      </span>
                      <CandidateRating
                        candidateId={r.id}
                        candidateName={r.candidate_name}
                        darkMode={darkMode}
                      />
                    </div>
                  </div>

                  <div className="referral-item-actions">
                    <button
                      className="btn-outline btn-sm"
                      onClick={() => setProfileReferral(r)}
                    >
                      <PersonSearchIcon sx={{ fontSize: 16, marginRight: '4px' }} />
                      View Profile
                    </button>

                    <button
                      className="btn-outline btn-sm"
                      onClick={() => setChatReferral(r)}
                    >
                      <ChatBubbleOutlineIcon sx={{ fontSize: 16, marginRight: '4px' }} />
                      Message
                    </button>

                    {/* Stage update dropdown */}
                    {r.status !== 'bounty_paid' && r.status !== 'rejected' && (
                      <select
                        className="btn-outline btn-sm"
                        value={r.status}
                        onChange={(e) => handleUpdateStage(r.id, e.target.value as ReferralSubmissionStatus)}
                        style={{ cursor: 'pointer', padding: '6px 10px', fontSize: '0.8rem' }}
                      >
                        <option value="submitted">New</option>
                        <option value="reviewing">Screening</option>
                        <option value="interviewing">Interview</option>
                        <option value="hired">Hired</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    )}

                    {r.status === 'hired' && (
                      <button
                        className="btn-primary btn-sm"
                        onClick={() => setCheckoutReferral(r)}
                      >
                        Pay Bounty
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Poster: No referrals yet */}
        {isPoster && receivedReferrals.length === 0 && (
          <div className="vacancy-detail-section">
            <div className="empty-state" style={{ padding: '2rem' }}>
              <h3>No referrals yet</h3>
              <p>Share this vacancy to start receiving candidate referrals.</p>
            </div>
          </div>
        )}

        {/* Non-poster: CTAs — different for scraped vs manual */}
        {!isPoster && (
          referralSuccess ? (
            <div className="success-banner">
              Your submission has been received successfully! You will be notified when there is an update.
            </div>
          ) : vacancy.source === 'scraped' ? (
            <div className="vacancy-cta-buttons">
              <button className="btn-primary btn-refer-cta" onClick={handleGetReferredClick}>
                <PersonSearchIcon sx={{ fontSize: 18, marginRight: '6px' }} />
                Get Referred
              </button>
              {vacancy.source_url && (
                <a
                  href={vacancy.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline btn-refer-cta"
                  style={{ textDecoration: 'none', justifyContent: 'center' }}
                >
                  <OpenInNewIcon sx={{ fontSize: 16, marginRight: '6px' }} />
                  View Original Posting
                </a>
              )}
            </div>
          ) : (
            <div className="vacancy-cta-buttons">
              <button className="btn-primary btn-refer-cta" onClick={handleApplyClick}>
                Apply for This Position
              </button>
              <button className="btn-outline btn-refer-cta" onClick={handleReferClick}>
                Refer Someone
              </button>
              <button className="btn-outline btn-refer-cta" onClick={handleGetReferredClick}>
                <PersonSearchIcon sx={{ fontSize: 18, marginRight: '6px' }} />
                Get Referred
              </button>
            </div>
          )
        )}
      </div>

      <ApplicationModal
        isOpen={showApplicationModal}
        onClose={() => setShowApplicationModal(false)}
        onSubmit={handleSubmitReferral}
        vacancyTitle={vacancy.title}
        companyName={vacancy.company_name}
        bountyAmount={vacancy.bounty_amount}
        bountyCurrency={vacancy.bounty_currency}
        darkMode={darkMode}
      />

      <ReferralSubmitModal
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
        onSubmit={handleSubmitReferral}
        vacancyTitle={vacancy.title}
        companyName={vacancy.company_name}
        bountyAmount={vacancy.bounty_amount}
        bountyCurrency={vacancy.bounty_currency}
        darkMode={darkMode}
      />

      {checkoutReferral && (
        <CheckoutModal
          isOpen={!!checkoutReferral}
          onClose={() => setCheckoutReferral(null)}
          onSuccess={handleHireSuccess}
          referralId={checkoutReferral.id}
          vacancyId={vacancy.id}
          candidateName={checkoutReferral.candidate_name}
          bountyAmount={vacancy.bounty_amount}
          bountyCurrency={vacancy.bounty_currency}
          darkMode={darkMode}
        />
      )}

      {chatReferral && (
        <ChatPanel
          referralId={chatReferral.id}
          vacancyTitle={vacancy.title}
          candidateName={chatReferral.candidate_name}
          isOpen={!!chatReferral}
          onClose={() => setChatReferral(null)}
          darkMode={darkMode}
        />
      )}

      {profileReferral && (
        <CandidateProfileModal
          referral={profileReferral}
          vacancy={vacancy}
          onClose={() => setProfileReferral(null)}
          darkMode={darkMode}
        />
      )}

      <GetReferredModal
        isOpen={showGetReferredModal}
        onClose={() => setShowGetReferredModal(false)}
        vacancyId={vacancy.id}
        vacancyTitle={vacancy.title}
        companyName={vacancy.company_name}
        darkMode={darkMode}
      />
    </div>
  );
};

export default VacancyDetail;
